import React from 'react';
import SEO from '@/components/SEO';
import { homeJsonLd } from '@/lib/structuredData';
import { CtaBand } from '@/components/site';
import { EnquiryButton } from '@/components/site/enquiry';
import {
  BusinessesBlock, FirstRoutes, FounderNote, HomeFaq, HomeHero, HomeStory, PricingBlock, ReportingBlock, VenuesBlock,
} from './homeParts';

/**
 * Home (`/`) under SLOT_MODEL: the approved Sept 26 2026 homepage (`site/index.html`).
 * Split hero (text and HOME-HERO-01) with the two audience actions and the guest link, the
 * three photo cards of the audience chooser (#choose, the header's "Get started"), the product story
 * (#how-it-works), local businesses (#businesses), venues (#venues), reporting
 * (#reporting), pricing with the calculator and the free listing (#pricing), the
 * founder note, the FAQ (#faqs) and the closing band. Page parts live in homeParts.jsx.
 *
 * Real numbers only: prices come from the calculator (GET /api/slots/tiers); no venue,
 * coaster, business or scan counts. Sales run through the enquiry dialog while payments
 * are off; the example offer is a labelled demo that creates no claim.
 */

export const HOME_SEO = {
  // Home speaks to advertisers (upgrade brief 2026-09-27 §2.7); /local-deals-near-me owns the guest intent.
  title: 'Tappd | Coaster advertising for local businesses in South Jersey',
  description:
    'Put your offer on the coasters at neighborhood bars and restaurants. Guests scan and claim it; venues get the coasters free.',
  url: '/',
};

/** The FAQ as shown (and as the FAQPage JSON-LD describes it). */
export const HOME_FAQ = [
  {
    q: 'Who pays for the venue’s coasters?',
    a: 'Local advertisers fund the coasters through paid placements. Tappd handles printing and delivery. Your venue uses the coasters and shares approximate guest counts.',
  },
  {
    q: 'Can a competitor advertise at the same venue?',
    a: 'Tappd offers one paid advertiser per category, per venue. The team will confirm your category definition and availability before you commit.',
  },
  {
    q: 'Which venues can I advertise at?',
    a: 'Placement depends on participating venues and available categories. Tell us your preferred town or area and we’ll help confirm the options. An enquiry does not reserve a slot.',
  },
  {
    q: 'When do the coasters arrive?',
    a: 'Coasters follow quarterly print runs. The team will confirm quantities, eligibility, and the next available delivery timing for your venue.',
  },
  {
    q: 'Is there a minimum commitment?',
    a: 'Standard and Premium have a three-month minimum on quarterly billing. Their annual plans are prepaid. Venue Sponsor has a 12-month commitment billed quarterly; the remainder of the term is payable on early exit.',
  },
  {
    q: 'Do guests need an app or an account?',
    a: 'No app or account is needed to browse. Guests use their email to claim an offer and receive a code to show at the business.',
  },
];

export default function HomeSlot() {
  return (
    <main id="main" className="text-navy">
      <SEO title={HOME_SEO.title} description={HOME_SEO.description} url={HOME_SEO.url} image="/images/og/home.jpg" jsonLd={homeJsonLd()} />

      <HomeHero />
      <FirstRoutes />
      <HomeStory />
      <BusinessesBlock />
      <VenuesBlock />
      <ReportingBlock />
      <PricingBlock />
      <FounderNote />
      <HomeFaq items={HOME_FAQ} />

      <CtaBand
        titleId="home-closing-title"
        eyebrow="There’s a place for you here"
        title={(
          <>
            Let’s put your <br />
            neighborhood <br />
            <em>on the table.</em>
          </>
        )}
        actions={(
          <>
            <EnquiryButton role="advertiser" variant="inverse">I want to advertise</EnquiryButton>
            <EnquiryButton role="venue" variant="ghost">I run a venue</EnquiryButton>
          </>
        )}
        note="small coaster. good company."
        image="HOME-CTA-01"
      />
    </main>
  );
}
